import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'

export const dataSourceMixin = {
  props: {
    dataSource: {
      type: Object,
      required: false,
      default: null
    }
  },
  data () {
    return {
      fetching: false,
      items: [],
      total: 0,
      endpoint: undefined,
      filter: undefined,
      pagination: {
        page: 1
      },
      quickSearchFromData: undefined,
      loadingFromData: false,
      useGlobalQuickSearch: false
    }
  },
  computed: {
    schema () {
      return this.dataSource && this.dataSource.schema
    },
    name () {
      return this.schema ? this.schema.name : 'item'
    },
    mainProperty () {
      return this.schemaMainProperty ? this.schemaMainProperty.name : 'name'
    },
    schemaMainProperty () {
      return this.schema && this.schema.fields.find(f => f.main)
    },
    idProperty () {
      return this.schemaIdProperty ? this.schemaIdProperty.name : 'id'
    },
    schemaIdProperty () {
      return this.schema && this.schema.fields.find(f => f.primaryKey)
    },
    debounceRefresh () {
      return debounce(() => {
        this.refresh()
      }, 350)
    },
    ...mapState('quickSearch', { quickSearchFromStore: 'quickSearch', loadingFromStore: 'loading' }),
    quickSearch: {
      get () {
        return this.useGlobalQuickSearch ? this.quickSearchFromStore : this.quickSearchFromData
      },
      set (newQuickSearch) {
        if (this.useGlobalQuickSearch) {
          this.setQuickSearch(newQuickSearch)
        } else {
          this.quickSearchFromData = newQuickSearch
        }
      }
    },
    loading: {
      get () {
        return this.useGlobalQuickSearch ? this.loadingFromStore : this.loadingFromData
      },
      set (newLoading) {
        if (this.useGlobalQuickSearch) {
          this.setLoading(newLoading)
        } else {
          this.loadingFromData = newLoading
        }
      }
    }
  },
  watch: {
    pagination: {
      deep: true,
      handler () {
        this.delayRefresh()
      }
    },
    endpoint: {
      immediate: true,
      handler () {
        this.delayRefresh()
      }
    },
    quickSearch: {
      immediate: true,
      handler (value, oldValue) {
        if (value !== oldValue) {
          this.pagination.page = 1
          this.delayRefresh()
        }
      }
    },
    filter: {
      immediate: true,
      handler (value, oldValue) {
        if (value !== oldValue) {
          this.pagination.page = 1
          this.delayRefresh()
        }
      }
    }
  },
  methods: {
    delayRefresh () {
      if (this.fetching) return
      this.loading = true
      this.debounceRefresh()
    },
    async refresh () {
      return this.load({
        endpoint: this.endpoint,
        quickSearch: this.quickSearch,
        filter: this.filter
      })
    },
    async load ({ endpoint = 'summary', quickSearch, filter, pagination }) {
      if (this.fetching) return
      this.endpoint = endpoint
      this.quickSearch = quickSearch
      this.filter = filter
      this.fetching = true
      try {
        if (pagination) {
          this.pagination = {
            ...this.pagination,
            page: 1,
            ...pagination
          }
        }
        if (!this.pagination.page) {
          this.pagination.page = 1
        }
        const { sortBy, page, rowsPerPage = 10, descending } = this.pagination
        const fieldSortBy = this.schema && this.schema.fields && this.schema.fields.find(f => f && f.name === sortBy)
        const sortByCustom = fieldSortBy && fieldSortBy.sortBy
        let sort = sortByCustom || sortBy
        if (sort) {
          sort = `${sort}${descending ? ',desc' : ''}`
        }

        if (this.dataSource && this.dataSource[endpoint]) {
          let data = await this.dataSource[endpoint]({
            page: page - 1,
            size: rowsPerPage,
            sort,
            quickSearch: quickSearch ? quickSearch.toLowerCase() : undefined,
            filter
          })
          this.total = data.totalElements
          this.items = data.content
        }
      } finally {
        this.loading = false
        this.fetching = false
      }
      return this.items
    },
    ...mapActions('quickSearch', ['setQuickSearch', 'setLoading'])
  }
}
